import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { HiXMark } from "react-icons/hi2";
import { activityService } from "../../services/activityService";
import { ActivityVariableDefinition } from "../../types/activity";
import { getUnitsForVariable, getDefaultUnit } from "../../utils/unitUtils";

interface CreateActivityTypeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  initialName: string;
  onActivityTypeCreated?: (
    activityTypeId: string,
    activityTypeName: string,
  ) => void;
}

// Available ActivityVariable names
const AVAILABLE_VARIABLES: string[] = [
  "Distance",
  "Incline",
  "Intensity",
  "Reps",
  "Rest",
  "Speed",
  "Time",
  "Weight",
];

const CreateActivityTypeDialog: React.FC<CreateActivityTypeDialogProps> = ({
  isOpen,
  onClose,
  initialName,
  onActivityTypeCreated,
}) => {
  const { currentUser, authState } = useAuth();
  const isImperial = authState.user?.isImperial ?? false;

  // Form state
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedVariables, setSelectedVariables] = useState<
    ActivityVariableDefinition[]
  >([]);
  const [image, setImage] = useState<string>("");

  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Image preview state
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);

  // State for custom variable input
  const [customVariableName, setCustomVariableName] = useState<string>("");
  const [customVariableUnit, setCustomVariableUnit] = useState<string>("units");
  const [showCustomVariableInput, setShowCustomVariableInput] =
    useState<boolean>(false);

  // Ref for custom variable input container
  const customVariableInputRef = useRef<HTMLDivElement>(null);

  // Initialize form with initial name
  useEffect(() => {
    if (isOpen && initialName) {
      setName(initialName);
    }
  }, [isOpen, initialName]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setName("");
      setDescription("");
      setSelectedVariables([]);
      setImage("");
      setError(null);
      setLoading(false);
      setImageLoading(false);
      setImageError(false);
      // Reset custom variable state
      setCustomVariableName("");
      setCustomVariableUnit("units");
      setShowCustomVariableInput(false);
    }
  }, [isOpen]);

  const toggleVariable = (variableName: string, unit?: string) => {
    setSelectedVariables((prev: ActivityVariableDefinition[]) => {
      const existingIndex = prev.findIndex((v) => v.name === variableName);

      if (existingIndex !== -1) {
        // Remove if already exists
        return prev.filter((_, idx) => idx !== existingIndex);
      } else {
        // Add with default unit if not provided (respecting user's measurement preference)
        const defaultUnit = unit || getDefaultUnit(variableName, isImperial);
        return [...prev, { name: variableName, defaultUnit }];
      }
    });
    setError(null);
  };

  const updateVariableUnit = (variableName: string, newUnit: string) => {
    setSelectedVariables((prev: ActivityVariableDefinition[]) => {
      return prev.map((v) =>
        v.name === variableName ? { ...v, defaultUnit: newUnit } : v,
      );
    });
  };

  const isVariableSelected = (variableName: string): boolean => {
    return selectedVariables.some((v) => v.name === variableName);
  };

  const getVariableUnit = (variableName: string): string => {
    const variable = selectedVariables.find((v) => v.name === variableName);
    return variable?.defaultUnit || getDefaultUnit(variableName, isImperial);
  };

  const addCustomVariable = () => {
    const trimmedName = customVariableName.trim();
    if (!trimmedName) {
      setError("Please enter a variable name");
      return;
    }

    // Check if variable already exists (case-insensitive)
    const existingVariableNames = selectedVariables.map((v) => v.name);
    const normalizedName = trimmedName.toLowerCase();
    const presetVariables = AVAILABLE_VARIABLES.map((v) => v.toLowerCase());

    if (
      existingVariableNames.some((v) => v.toLowerCase() === normalizedName) ||
      presetVariables.includes(normalizedName)
    ) {
      setError("This variable already exists");
      return;
    }

    // Add the custom variable with its unit
    toggleVariable(trimmedName, customVariableUnit);
    setCustomVariableName("");
    setCustomVariableUnit("units");
    setShowCustomVariableInput(false);
    setError(null);
  };

  const cancelCustomVariable = () => {
    setCustomVariableName("");
    setCustomVariableUnit("units");
    setShowCustomVariableInput(false);
    setError(null);
  };

  // Handle clicks outside custom variable input to cancel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showCustomVariableInput &&
        customVariableInputRef.current &&
        !customVariableInputRef.current.contains(event.target as Node)
      ) {
        cancelCustomVariable();
      }
    };

    if (showCustomVariableInput) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCustomVariableInput]);

  const handleCreateActivityType = async () => {
    if (!currentUser?.uid) {
      setError("You must be logged in to create exercises");
      return;
    }

    if (selectedVariables.length === 0) {
      setError("Please select at least one variable");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const activityTypeData = {
        name: name,
        description: description,
        variables: selectedVariables, // Now ActivityVariableDefinition[]
        image: image,
      };

      const activityTypeId = await activityService.createActivityType(
        currentUser.uid,
        activityTypeData,
      );

      console.log(
        "Activity type created successfully with ID:",
        activityTypeId,
      );

      if (onActivityTypeCreated) {
        onActivityTypeCreated(activityTypeId, name);
      }

      onClose();
    } catch (error) {
      console.error("Error creating activity type:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to create exercise",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCreateActivityType();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[10002] p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-base-100 w-full h-full md:rounded-xl md:shadow-xl sm:max-w-2xl sm:w-full md:h-auto md:max-h-[70vh] flex flex-col relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex bg-base-100 items-center justify-between p-8 pb-0 flex-shrink-0 md:rounded-t-xl">
          <div className="flex items-center gap-2">
            <h2 className="text-xl text-base-content font-grotesk">
              Create <strong>Exercise</strong>
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
          >
            <HiXMark className="w-5 h-5" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-visible flex flex-col">
          <form
            onSubmit={handleSubmit}
            className="p-4 px-8 space-y-4 pb-0 bg-base-100"
          >
            {/* Activity Type Name */}
            <div className="new-form-group-rounded">
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                placeholder=" "
                className="form-element-rounded"
                disabled={loading}
                required
              />
              <label>Exercise Name</label>
            </div>

            {/* Description */}
            <div className="new-form-group-rounded">
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setError(null);
                }}
                placeholder=" "
                rows={4}
                className="form-element-rounded resize-none"
                style={{
                  height: "100px",
                }}
                disabled={loading}
              />
              <label>Description</label>
            </div>

            {/* Activity Variables */}
            <div className="form-control">
              <div className="mb-2">
                <label className="text-sm font-bold text-base-content/80 block font-heading">
                  Variables
                </label>
                <p className="text-xs text-base-content/60 mb-4">
                  What data do you want to track for each exercise
                </p>

                {/* Preset Variables */}
                <div className="mb-4 space-y-2">
                  {AVAILABLE_VARIABLES.map((variableName) => {
                    const selected = isVariableSelected(variableName);
                    const availableUnits = getUnitsForVariable(
                      variableName,
                      isImperial,
                    );
                    const currentUnit = getVariableUnit(variableName);

                    return (
                      <div
                        key={variableName}
                        className={`p-3 rounded-lg border-2 transition-colors ${
                          selected
                            ? "border-base-100 bg-base-100"
                            : "border-base-200 bg-base-100"
                        }`}
                      >
                        <label className="flex items-center space-x-2 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleVariable(variableName)}
                            disabled={loading}
                            className="checkbox"
                          />
                          <span className="text-sm font-bold text-base-content">
                            {variableName}
                          </span>
                        </label>

                        {selected && (
                          <div className="ml-6">
                            <label className="text-xs text-base-content/80 mb-1 block">
                              Default Unit
                            </label>
                            <select
                              value={currentUnit}
                              onChange={(e) =>
                                updateVariableUnit(variableName, e.target.value)
                              }
                              disabled={loading}
                              className="text-xs w-full px-2 py-1 border border-base-300 rounded bg-base-100 focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-300 font-grotesk"
                            >
                              {availableUnits.map((unit) => (
                                <option key={unit} value={unit}>
                                  {unit}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Custom Variables */}
                {selectedVariables
                  .filter(
                    (variable) => !AVAILABLE_VARIABLES.includes(variable.name),
                  )
                  .map((customVariable) => {
                    const availableUnits = getUnitsForVariable(
                      customVariable.name,
                      isImperial,
                    );

                    return (
                      <div
                        key={customVariable.name}
                        className="mb-3 p-3 rounded-lg border-2 border-blue-200 bg-blue-500/10"
                      >
                        <label className="flex items-center space-x-2 cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={true}
                            onChange={() => toggleVariable(customVariable.name)}
                            disabled={loading}
                            className="checkbox"
                          />
                          <span className="text-sm text-blue-700 font-grotesk font-medium">
                            {customVariable.name} (Custom)
                          </span>
                        </label>

                        <div className="ml-6">
                          <label className="text-xs text-blue-600 font-grotesk mb-1 block">
                            Default Unit:
                          </label>
                          <select
                            value={customVariable.defaultUnit}
                            onChange={(e) =>
                              updateVariableUnit(
                                customVariable.name,
                                e.target.value,
                              )
                            }
                            disabled={loading}
                            className="text-xs w-full px-2 py-1 border border-blue-300 rounded bg-base-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-grotesk"
                          >
                            {availableUnits.map((unit) => (
                              <option key={unit} value={unit}>
                                {unit}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}

                {/* Add Custom Variable */}
                {!showCustomVariableInput ? (
                  <button
                    type="button"
                    onClick={() => setShowCustomVariableInput(true)}
                    disabled={loading}
                    className="flex items-center space-x-2 p-2 rounded-lg border-2 border-dashed border-base-300 hover:border-base-300 hover:bg-base-100 transition-colors w-full text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      className="w-4 h-4 text-base-content/50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span className="text-sm text-base-content/80 font-grotesk">
                      Add custom variable
                    </span>
                  </button>
                ) : (
                  <div
                    ref={customVariableInputRef}
                    className="p-3 border border-base-300 rounded-lg bg-base-200 space-y-2"
                  >
                    <div>
                      <label className="text-xs text-base-content/80 font-grotesk mb-1 block font-medium">
                        Variable Name
                      </label>
                      <input
                        type="text"
                        value={customVariableName}
                        onChange={(e) => setCustomVariableName(e.target.value)}
                        placeholder="e.g., Cadence, Power, Elevation"
                        className="w-full px-3 py-2 text-sm border bg-base-100 border-base-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-grotesk"
                        disabled={loading}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && customVariableName.trim()) {
                            e.preventDefault();
                            addCustomVariable();
                          } else if (e.key === "Escape") {
                            cancelCustomVariable();
                          }
                        }}
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="text-xs text-base-content/80 font-grotesk mb-1 block font-medium">
                        Default Unit
                      </label>
                      <input
                        type="text"
                        value={customVariableUnit}
                        onChange={(e) => setCustomVariableUnit(e.target.value)}
                        placeholder="e.g., bpm, watts, m"
                        className="w-full px-3 py-2 text-sm border bg-base-100 border-base-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-grotesk"
                        disabled={loading}
                      />
                    </div>

                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={addCustomVariable}
                        disabled={loading || !customVariableName.trim()}
                        className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-grotesk font-medium"
                      >
                        Add Variable
                      </button>
                      <button
                        type="button"
                        onClick={cancelCustomVariable}
                        disabled={loading}
                        className="px-4 py-2 text-sm text-base-content/80 bg-base-100 border border-base-300 rounded hover:bg-base-100 disabled:opacity-50 disabled:cursor-not-allowed font-grotesk"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {selectedVariables.length === 0 && (
                  <p className="text-xs text-base-content/60 mt-4 font-grotesk">
                    *Please select at least one variable
                  </p>
                )}
              </div>
            </div>

            {/* Image URL (Optional) */}
            {/* <div>
            <label className="text-sm sm:text-base font-medium text-base-content/80 mb-2 block font-heading">
              Image URL (Optional)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3"> */}
            {/* URL Input - Left Side */}
            {/* <div className="sm:col-span-2">
                <div className="new-form-group w-full">
                  <input
                    type="url"
                    id="activityTypeImage"
                    value={image}
                    onChange={(e) => {
                      const newUrl = e.target.value.trim();
                      setImage(newUrl);
                      // Reset error state when user starts typing
                      if (newUrl && imageError) {
                        setImageError(false);
                        setImageLoading(true);
                      }
                    }}
                    className="form-element"
                    placeholder="https://example.com/image.jpg"
                    disabled={loading}
                  />
                  <label htmlFor="activityTypeImage">Image URL</label>
                </div>
                {imageError && image && (
                  <p className="text-xs text-red-500 font-grotesk mt-1">
                    Unable to load image from this URL. Please verify the URL is
                    correct.
                  </p>
                )}
              </div> */}

            {/* Image Preview - Right Side with 16:9 ratio */}
            {/* <div className="sm:col-span-1">
                {image && image.trim() ? (
                  <div className="relative w-full">
                    <div className="aspect-video w-full rounded-lg overflow-hidden border-2 border-base-200 bg-base-200 relative">
                      {imageLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-base-200">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                      )}
                      {imageError ? (
                        <div className="w-full h-full flex flex-col items-center justify-center p-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-8 h-8 text-red-400 mb-1"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                            />
                          </svg>
                          <p className="text-xs text-red-500 font-grotesk text-center">
                            Failed
                          </p>
                        </div>
                      ) : (
                        <img
                          src={image}
                          alt="Activity preview"
                          className={`w-full h-full object-cover ${
                            imageLoading ? "opacity-0" : "opacity-100"
                          } transition-opacity`}
                          onLoad={() => {
                            setImageLoading(false);
                            setImageError(false);
                          }}
                          onError={() => {
                            setImageLoading(false);
                            setImageError(true);
                          }}
                          onLoadStart={() => {
                            setImageLoading(true);
                            setImageError(false);
                          }}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setImage("");
                        setImageError(false);
                        setImageLoading(false);
                      }}
                      disabled={loading}
                      className="absolute -top-2 -right-2 p-1 bg-base-100 rounded-full shadow-md hover:bg-base-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed z-10"
                      title="Remove image"
                    >
                      <HiXMark className="w-3 h-3 text-base-content/80" />
                    </button>
                  </div>
                ) : (
                  <div className="aspect-video w-full rounded-lg border-2 border-dashed border-base-300 bg-base-100 flex items-center justify-center">
                    <div className="text-center p-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-8 h-8 text-base-content/50 mx-auto mb-1"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                        />
                      </svg>
                      <p className="text-xs text-base-content/60 font-grotesk">
                        No image
                      </p>
                    </div>
                  </div>
                )}
              </div> */}
            {/* </div>
          </div> */}

            {/* Error Message */}
            {error && (
              <div className="text-red-600 text-sm sm:text-base bg-red-500/10 border border-red-200 rounded-lg p-3 font-grotesk">
                {error}
              </div>
            )}
          </form>

          {/* Save Button - At bottom of content */}
          <div className="pb-20 px-8 mt-auto pt-10">
            <div className="flex items-center overflow-hidden">
              <button
                type="button"
                onClick={handleCreateActivityType}
                disabled={
                  loading || !name.trim() || selectedVariables.length === 0
                }
                className="bg-primary text-white rounded-full py-3 px-6 font-grotesk font-bold disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-sm mr-2"></span>
                    Creating...
                  </>
                ) : (
                  "Create Exercise"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CreateActivityTypeDialog;
